import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '~/lib/cn';
import { useSession, type KybStatus, type Me } from '~/lib/session-context';

/**
 * Breadcrumb + icon strip. Business name on the left comes from
 * /dashboard/me; the right-side icon row includes an avatar that opens a
 * tiny menu with Sign out.
 */
export function TopBar(): JSX.Element {
  const { me, kybStatus } = useSession();
  return (
    <div className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-rule-hairline bg-surface-canvas/85 px-10 backdrop-blur-sm">
      <Link
        to="/"
        className="text-[14.5px] font-medium text-ink-secondary hover:text-ink-primary"
      >
        Seven
      </Link>
      <span className="text-ink-watermark">/</span>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[14.5px] font-semibold text-ink-primary hover:bg-surface-margin"
      >
        <span>{me?.tenant.name ?? '…'}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-ink-watermark"
        >
          <path d="M2 4l3 3 3-3" />
        </svg>
      </button>
      {me && <TenantStatus status={me.tenant.status} kybStatus={kybStatus} />}

      <div className="ml-auto flex items-center gap-1">
        <IconButton label="Notifications">
          <path d="M4.5 7a3.5 3.5 0 1 1 7 0v3l1 2H3.5l1-2V7z" />
          <path d="M6.5 13.5a1.5 1.5 0 0 0 3 0" />
        </IconButton>
        <IconButton label="Settings">
          <circle cx="8" cy="8" r="2" />
          <path d="M8 2v2M8 12v2M2 8h2M12 8h2M4 4l1.5 1.5M10.5 10.5L12 12M4 12l1.5-1.5M10.5 5.5L12 4" />
        </IconButton>
        <AvatarMenu me={me} />
      </div>
    </div>
  );
}

function TenantStatus({
  status,
  kybStatus,
}: {
  status: Me['tenant']['status'];
  kybStatus: KybStatus;
}): JSX.Element | null {
  if (status === 'active') return null;
  const { copy, tone } = pillCopy(status, kybStatus);
  return (
    // Quiet secondary route to /kyb; the loud one is VerificationBanner.
    <Link
      to="/kyb"
      aria-label={`${copy} — open business verification`}
      className={`font-mono ml-1 inline-flex items-center rounded-full px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[0.08em] transition-opacity hover:opacity-80 ${tone}`}
    >
      {copy}
    </Link>
  );
}

function pillCopy(
  status: Me['tenant']['status'],
  kyb: KybStatus,
): { copy: string; tone: string } {
  if (status === 'suspended') {
    return { copy: 'Suspended', tone: 'bg-stop-tint text-stop' };
  }
  if (kyb === 'submitted') {
    return { copy: 'KYB Submitted', tone: 'bg-footnote-tint text-footnote' };
  }
  if (kyb === 'in_review') {
    return { copy: 'KYB In review', tone: 'bg-footnote-tint text-footnote' };
  }
  if (kyb === 'rejected') {
    return { copy: 'KYB Rejected', tone: 'bg-stop-tint text-stop' };
  }
  if (kyb === 'approved') {
    // Tenant row hasn't propagated to active yet; the chrome will catch up.
    return { copy: 'KYB Approved', tone: 'bg-tally-tint text-tally' };
  }
  return { copy: 'Pending KYB', tone: 'bg-notice-tint text-notice' };
}

function AvatarMenu({ me }: { me: Me | null }): JSX.Element {
  const { signOut } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const initials = me
    ? `${me.user.firstName.charAt(0)}${me.user.lastName.charAt(0)}`.toUpperCase() ||
      me.user.email.charAt(0).toUpperCase()
    : '…';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full border border-rule-hairline bg-surface-margin text-[11px] font-semibold text-ink-secondary hover:border-rule-divider hover:text-ink-primary',
          open && 'border-rule-divider text-ink-primary',
        )}
      >
        {initials}
      </button>
      {open && me && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] w-[220px] overflow-hidden rounded-lg border border-rule-hairline bg-surface-plate shadow-lift-md"
        >
          <div className="border-b border-rule-hairline px-3 py-2.5">
            <div className="text-[14px] font-semibold text-ink-primary">
              {me.user.firstName} {me.user.lastName}
            </div>
            <div className="truncate text-[13px] text-ink-watermark">
              {me.user.email}
            </div>
          </div>
          <button
            role="menuitem"
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-[14px] text-ink-primary hover:bg-surface-margin"
          >
            Sign out
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-ink-watermark"
            >
              <path d="M10 11l3-3-3-3M13 8H5M5 2H3v12h2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function IconButton({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </button>
  );
}
